import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { identifyFullStoryUser } from '../../../utils/fullstory';
import { handleUserRedirect } from '../../../utils/userRedirect';
import {
  broadcastAuthEvent,
  subscribeToAuthEvents,
  AUTH_EVENT_SIGNED_IN,
  AUTH_EVENT_SIGNED_IN_ACK,
} from '../../../utils/authBroadcast';

import LoadingScreen from '../../system/loading_screen';

// How long to wait for another tab (the original /member/login tab the user
// signed up in) to acknowledge our broadcast. If we hear back, that tab will
// take over and we just show a "you can close this tab" message; if not, we
// fall back to the normal redirect for this tab.
const ACK_WAIT_MS = 1500;

export default function AutoRedirect({ currentUser }) {
  const history = useHistory();
  const [acknowledgedByOtherTab, setAcknowledgedByOtherTab] = useState(false);

  useEffect(() => {
    if (!currentUser || !currentUser.id) return;

    identifyFullStoryUser(currentUser);

    let acked = false;
    const unsubscribe = subscribeToAuthEvents((event) => {
      if (event.type !== AUTH_EVENT_SIGNED_IN_ACK) return;
      acked = true;
      setAcknowledgedByOtherTab(true);
      // The original tab now owns the authenticated session. Try to close
      // this tab so the user doesn't have to. Browsers permit window.close()
      // for tabs without back-history (which a magic-link tab usually has,
      // since the redirect to /auth/sign-in-token uses 302). If the browser
      // refuses, the "you can close this tab" message stays visible.
      window.close();
    });

    broadcastAuthEvent(AUTH_EVENT_SIGNED_IN, { userId: currentUser.id });

    const timer = setTimeout(() => {
      unsubscribe();
      if (!acked) {
        const isImpersonation = Boolean(currentUser.true_user && currentUser.true_user.id !== currentUser.id);
        handleUserRedirect(currentUser, history, {
          isImpersonation,
          returnTo: '',
        });
      }
    }, ACK_WAIT_MS);

    return () => {
      clearTimeout(timer);
      unsubscribe();
    };
  }, [currentUser, history]);

  if (acknowledgedByOtherTab) {
    return (
      <div
        style={{
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
        }}
      >
        <div style={{ maxWidth: '440px', textAlign: 'center' }}>
          <h2 style={{ color: '#173959', marginBottom: '12px' }}>You're signed in!</h2>
          <p style={{ color: '#425b76', fontSize: '16px', lineHeight: '1.5' }}>
            We've signed you in on the tab where you started. You can safely close this tab and return there.
          </p>
        </div>
      </div>
    );
  }

  return <LoadingScreen />;
}
